import { Box, Skeleton } from "@mui/material";
import { ReactNode } from "react";

// Placeholders for the Strategy Report's filter rows while their values load
// (task 6134). Each shape is the height of what replaces it — MUI's small
// Chip, small IconButton with a small icon, and small outlined Select — so
// a row is its final height from the first paint and the card never resizes
// as the values arrive one by one.
export const CHIP_HEIGHT = 24;
export const ICON_BUTTON_SIZE = 30;
export const SELECT_HEIGHT = 40;

export const ChipSkeleton = ({ width }: { width: number }) => (
  <Skeleton
    variant="rounded"
    width={width}
    height={CHIP_HEIGHT}
    sx={{ borderRadius: `${CHIP_HEIGHT / 2}px`, flexShrink: 0 }}
  />
);

export const IconButtonSkeleton = () => (
  <Skeleton
    variant="circular"
    width={ICON_BUTTON_SIZE}
    height={ICON_BUTTON_SIZE}
    sx={{ flexShrink: 0 }}
  />
);

// Narrows, down to minWidth, before its row would have to wrap.
export const SelectSkeleton = ({
  width,
  minWidth = width,
}: {
  width: number;
  minWidth?: number;
}) => (
  <Skeleton
    variant="rounded"
    width={width}
    height={SELECT_HEIGHT}
    sx={{ flexShrink: minWidth < width ? 1 : 0, minWidth }}
  />
);

interface FilterSkeletonRowProps {
  // Names what is loading, for assistive tech: the shapes themselves say
  // nothing.
  label: string;
  gap: number;
  // When the loaded row can be taller than its tallest shape.
  minHeight?: number;
  // False when the loaded row usually fits one line, so the placeholder
  // keeps to one too, its shapes narrowing instead (task 6149).
  wrap?: boolean;
  children: ReactNode;
}

export const FilterSkeletonRow = ({
  label,
  gap,
  minHeight,
  wrap = true,
  children,
}: FilterSkeletonRowProps) => (
  <Box
    role="progressbar"
    aria-label={label}
    display="flex"
    alignItems="center"
    // Wraps where the loaded row wraps, so the two take the same number of
    // lines.
    flexWrap={wrap ? "wrap" : "nowrap"}
    gap={gap}
    minHeight={minHeight}
  >
    {children}
  </Box>
);
